import { Component, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { format } from 'date-fns';
import { MeteorObservable } from 'meteor-rxjs';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  Observable,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { Customers } from 'server/shared/collections/customers';
import { Employments } from 'server/shared/collections/employments';
import { Jobs } from 'server/shared/collections/jobs';
import { OrganisationRequests } from 'server/shared/collections/organisationRequests';
import { Users } from 'server/shared/collections/users';
import { checkIfRested } from 'server/shared/helpers/eligibilityChecks';
import { getPossibleEmploymentsQuery } from 'server/shared/helpers/employment/getPossibleEmploymentsQuery';
import { getDispoActionRequiredJobQuery } from 'server/shared/helpers/jobs-query/getDispoActionRequiredJobQuery';
import { getJobsMongoQuery } from 'server/shared/helpers/jobs-query/getJobsMongoQuery';
import { getNewUnixTimestamp } from 'server/shared/helpers/removeTimeZoneOffset';
import { Customer } from 'server/shared/models/customer';
import { DispoEmploymentTabIdEnum } from 'server/shared/models/DispoEmploymentTabId.enum';
import {
  Employment,
  EmploymentStatusEnum,
} from 'server/shared/models/employment';
import { EmploymentInviteeStatusEnum } from 'server/shared/models/employmentInviteeStatusEnum';
import { Invitee } from 'server/shared/models/invitee';
import { Job } from 'server/shared/models/job';
import { InviteeStatusEnum } from 'server/shared/models/jobInviteeStatusEnum';
import { JobStatusEnum } from 'server/shared/models/jobStatusEnum';
import { PoolerEmploymentTabIdEnum } from 'server/shared/models/PoolerEmploymentTabIdEnum';
import { PoolerJobsTabIdEnum } from 'server/shared/models/PoolerJobsTabId.enum';
import { RoleEnum } from 'server/shared/models/role.enum';
import { PoolerUser, User } from 'server/shared/models/user';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { OrganisationService } from 'src/app/services/organisation.service';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { rxFilterIsNotEmpty } from '../../../helpers/filterIsNotEmpty';
import { CustomerService } from '../../services/customer.service';
import { UserService } from '../../services/user.service';
import { SubscriptionService } from '../../shared/subscription.service';

export type PossibleJobsTab = 'possible-jobs' | 'possible-employments';

@Component({
  selector: 'users-dashboard',
  templateUrl: 'dashboard.component.html',
  styleUrls: ['dashboard.component.scss'],
})
export class UsersDashboardComponent {
  user?: User;
  poolerUser$ = this.userService.readyPooler$;

  loggedInRole$ = this.userService.role$;
  organisationRequests = OrganisationRequests.find().pipe(
    rxNgZoneRun(this.ngZone),
  );
  nextJob?: Job;
  pendingTimetrackingJob?: Job;
  nextEmployment?: Employment;
  newJobs?: Observable<Job[]>;
  newEmployments?: Observable<Employment[]>;
  myUserSubscription?: Subscription;
  organisationRequestSubscription?: Subscription;
  meteorLiveQueryHandlers: Meteor.LiveQueryHandle[] = [];
  dispoCustomer?: Customer;
  checkOwnAvailabilityForPossibleJobs: boolean = false;
  roleEnum = RoleEnum;
  jobStatusEnum = JobStatusEnum;
  dispoEmploymentStatusEnum = DispoEmploymentTabIdEnum;
  poolerJobStatusEnum = PoolerJobsTabIdEnum;
  poolerEmploymentStatusEnum = PoolerEmploymentTabIdEnum;

  userTracker?: Tracker.Computation;
  jobsTracker?: Tracker.Computation;
  possibleJobsTracker?: Tracker.Computation;

  jobsObserver?: Meteor.LiveQueryHandle;

  possibleJobsTabs: {
    label: string;
    value: PossibleJobsTab;
    svgIcon: string;
  }[] = [
    {
      label: 'possibleJobs',
      value: 'possible-jobs',
      svgIcon: 'content_paste',
    },
    {
      label: 'possibleEmployments',
      value: 'possible-employments',
      svgIcon: 'work',
    },
  ];

  possibleJobsTab$ = new BehaviorSubject<PossibleJobsTab>('possible-jobs');

  possibleEmployments$ = combineLatest([
    this.possibleJobsTab$,
    this.userService.readyPooler$,
    this.customerService.poolerCustomers$.pipe(rxFilterIsNotEmpty()),
  ]).pipe(
    filter(([tab]) => tab === 'possible-employments'),
    switchMap(([tab, currentUser, poolerCustomers]) =>
      Employments.find(
        getPossibleEmploymentsQuery(currentUser, poolerCustomers),
      ),
    ),
  );

  possibleJobsOwnAvailability$ = new BehaviorSubject<boolean>(false);

  unfilteredPossibleJobs$: Observable<Job[]> =
    this.possibleJobsOwnAvailability$.pipe(
      switchMap((ownAvailability) => {
        return Jobs.find(
          {
            status: JobStatusEnum.OPEN,
            'invitees.userId': { $ne: Meteor.userId() },
          },
          {
            sort: { date: 1 },
            limit: 500,
            transform: this.transformPossibleJob.bind(this),
          },
        );
      }),
    );

  possibleJobs$: Observable<Job[]> = combineLatest([
    this.possibleJobsTab$,
    this.unfilteredPossibleJobs$,
    this.userService.readyPooler$,
    this.customerService.poolerCustomers$.pipe(rxFilterIsNotEmpty()),
    this.organisationService.readyOrganisations$,
  ]).pipe(
    filter(([tab]) => tab === 'possible-jobs'),
    switchMap(
      ([tab, possibleJobs, currentUser, poolerCustomers, organisations]) => {
        const filteredJobs = possibleJobs.filter((job) => {
          let ownCustomer = poolerCustomers.find(
            (customer) => customer._id === job.yourself?.customer.id,
          );

          if (!ownCustomer) {
            return false;
          }

          let organisation = organisations.find(
            (organisation) => organisation._id === job.organisation.id,
          );
          if (!organisation) {
            return false;
          }
          let department = organisation?.departments.find(
            (department) => department.id === job.department.id,
          );
          if (!department) {
            return false;
          }
          let isAllowedOnJob = true;

          if (
            department?.blacklistedUsers?.some(
              (user) => user.id === Meteor.userId(),
            )
          ) {
            isAllowedOnJob = false;
          }

          if (
            organisation?.customerInformation?.[ownCustomer._id]?.tags
              ?.length &&
            !organisation.customerInformation[ownCustomer._id].tags?.some(
              (tag) =>
                currentUser.customerInformation![ownCustomer!._id]?.tags?.some(
                  (userTag) => userTag.id === tag.id,
                ),
            )
          ) {
            isAllowedOnJob = false;
          }

          if (
            !checkIfRested({
              user: currentUser,
              jobDate: job.date,
              jobShifts: job.shifts,
              requiredPause: ownCustomer.requiredPause,
              blockingOnly: true,
            }).hasRested
          ) {
            isAllowedOnJob = false;
          }

          return isAllowedOnJob;
        });

        return [filteredJobs];
      },
    ),
    switchMap((jobsArr) =>
      Array.isArray(jobsArr[0]) ? [jobsArr[0]] : [jobsArr],
    ),
  );

  constructor(
    private ngZone: NgZone,
    private route: ActivatedRoute,
    private router: Router,
    private subscriptionService: SubscriptionService,
    public userService: UserService,
    private customerService: CustomerService,
    private organisationService: OrganisationService,
  ) {
    this.subscriptionService.jobs.subscribe();
    this.subscriptionService.employments.subscribe();
    this.subscriptionService.poolerEmployments.subscribe();
    this.subscriptionService.possibleEmployments.subscribe();
    this.subscriptionService.pendingTimetrackingJobs.subscribe();
    this.subscriptionService.organisationRequests.subscribe();

    this.poolerUser$.pipe(take(1)).subscribe((user) => {
      if (!user.hasJobAssignments) {
        this.possibleJobsTab$.next('possible-employments');
      }
    });
  }

  ngOnInit() {
    this.myUserSubscription = MeteorObservable.subscribe('myProfile').subscribe(
      () => {
        this.userTracker = Tracker.autorun(() => {
          this.ngZone.run(() => {
            this.user = Users.findOne(Meteor.userId());
            if (this.user?.role === RoleEnum.REQUESTER) {
              this.router.navigate(['job-requests']);
            }
          });
        });
      },
    );

    this.jobsTracker = Tracker.autorun(() => {
      this.ngZone.run(() => {
        const userId = Meteor.userId();
        if (!userId) return;
        let user = Users.findOne(userId);
        if (Session.get('role') == undefined || !user) {
          return;
        }
        this.user = user;
        if (user.role == RoleEnum.DISPO)
          this.dispoCustomer = Customers.findOne(user.customerId);
        let jobQuery: Mongo.Selector<Job> & { $and?: Mongo.Selector<Job>[] } =
          {};
        let employmentQuery: Mongo.Selector<Employment> = {};

        if (this.isPooler) {
          jobQuery = {
            ['$or']: [
              {
                status: JobStatusEnum.OPEN,
                invitees: {
                  $elemMatch: {
                    userId,
                    status: {
                      $in: [InviteeStatusEnum.OPEN],
                    },
                    hide: { $ne: true },
                  },
                },
              },
              {
                status: JobStatusEnum.CLOSED,
                invitees: {
                  $elemMatch: {
                    userId,
                    status: {
                      $in: [InviteeStatusEnum.OPEN, InviteeStatusEnum.DECLINED],
                    },
                    hide: { $ne: true },
                  },
                },
              },
              {
                status: {
                  $in: [JobStatusEnum.ARCHIVED, JobStatusEnum.CANCELED],
                },
                cancelReason: { $exists: true },
                invitees: {
                  $elemMatch: {
                    userId,
                    status: {
                      $in: [
                        InviteeStatusEnum.OPEN,
                        InviteeStatusEnum.CANCELED,
                        InviteeStatusEnum.DECLINED,
                      ],
                    },
                    hide: { $ne: true },
                  },
                },
              },
            ],
          };
          employmentQuery = {
            status: EmploymentStatusEnum.OPEN,
            invitees: {
              $elemMatch: {
                userId,
                status: EmploymentInviteeStatusEnum.OPEN,
              },
            },
          };
        } else if (user.role == RoleEnum.DISPO) {
          let defaultFilter: any = {};
          if (user.defaultFilters && user.defaultFilters[JobStatusEnum.OPEN]) {
            defaultFilter = user.defaultFilters[JobStatusEnum.OPEN];
            defaultFilter.state = JobStatusEnum.OPEN;
          }
          const actionRequiredQuery = getDispoActionRequiredJobQuery(
            this.dispoCustomer?._id || '',
          );
          jobQuery = getJobsMongoQuery(defaultFilter, user);
          if (!jobQuery.$and) {
            jobQuery['$and'] = [];
          }
          jobQuery['$and'].push(actionRequiredQuery);
          employmentQuery = {
            status: EmploymentStatusEnum.OPEN,
            $or: [
              {
                invitees: {
                  $elemMatch: {
                    status: {
                      $in: [EmploymentInviteeStatusEnum.AVAILABLE],
                    },
                    'customer.id': this.dispoCustomer?._id || '',
                  },
                },
              },
              {
                invitees: {
                  $not: {
                    $elemMatch: {
                      status: EmploymentInviteeStatusEnum.OPEN,
                      'customer.id': this.dispoCustomer?._id || '',
                    },
                  },
                },
              },
            ],
          };
        }
        this.newJobs = Jobs.find(jobQuery, {
          sort: { status: 1, date: 1 },
          limit: 3,
          transform: this.transform.bind(this),
        });
        this.newEmployments = Employments.find(employmentQuery, {
          sort: { startDate: 1 },
          limit: 3,
        });
        this.nextJob = Jobs.findOne(
          {
            status: JobStatusEnum.CLOSED,
            invitees: {
              $elemMatch: {
                userId: Meteor.userId(),
                status: {
                  $in: [InviteeStatusEnum.BOOKED],
                },
              },
            },
          },
          {
            sort: { date: 1 },
            transform: this.transform.bind(this),
          },
        );
        let isMobile =
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent,
          ) ||
          'ontouchstart' in window ||
          window.innerWidth <= 768;
        this.pendingTimetrackingJob = Jobs.findOne(
          {
            date: {
              $lte: format(new Date(), dbDayDateToken),
            },
            invitees: {
              $elemMatch: {
                userId: Meteor.userId(),
                status: {
                  $in: [InviteeStatusEnum.BOOKED],
                },
                timeTrackings: {
                  $elemMatch: {
                    approvedAt: { $exists: false },
                    $or: [
                      {
                        toBeApprovedAt: { $exists: false },
                      },
                      {
                        toBeApprovedAt: isMobile
                          ? { $gte: getNewUnixTimestamp() - 3600 }
                          : { $exists: false },
                      },
                    ],
                    disabled: { $ne: true },
                  },
                },
              },
            },
          },
          {
            sort: { date: 1 },
            transform: this.transform.bind(this),
          },
        );

        this.nextEmployment = Employments.findOne(
          {
            status: {
              $in: [EmploymentStatusEnum.OPEN, EmploymentStatusEnum.CLOSED],
            },
            invitees: {
              $elemMatch: {
                userId: Meteor.userId(),
                status: EmploymentInviteeStatusEnum.BOOKED,
              },
            },
          },
          {
            sort: { startDate: 1 },
          },
        );
      });
    });
  }
  get isPooler(): boolean {
    return Session.get('role') == RoleEnum.POOL;
  }

  get jobListType(): string {
    return RoleEnum[Session.get('role')];
  }

  denyOrAcceptPoolRequest(customerId: string, accept: boolean) {
    Meteor.call(
      'acceptOrDenyPoolRequest',
      customerId,
      accept,
      (error: Meteor.Error) => {
        if (error?.error) throw new Meteor.Error(error.error);
      },
    );
  }

  denyOrAcceptOrganisationRequest(
    organisationRequestId: string,
    accept: boolean,
  ) {
    Meteor.call(
      'acceptOrDenyOrganisationRequest',
      organisationRequestId,
      accept,
      (error: Meteor.Error) => {
        if (error?.error) throw new Meteor.Error(error.error);
      },
    );
  }

  toggleCheckOwnAvailability() {
    this.checkOwnAvailabilityForPossibleJobs =
      !this.checkOwnAvailabilityForPossibleJobs;
    this.possibleJobsOwnAvailability$.next(
      this.checkOwnAvailabilityForPossibleJobs,
    );
  }

  transformPossibleJob(doc: Job) {
    if (
      this.user?.role === RoleEnum.POOL &&
      (!this.checkOwnAvailabilityForPossibleJobs ||
        this.user?.availabilities?.findIndex(
          (availability) =>
            availability.date === doc.date &&
            doc.shifts.every(
              (shift) => availability.shifts?.[shift.shift] === 'AVAILABLE',
            ),
        ) !== -1)
    ) {
      doc.ownAvailabilityChecked = this.checkOwnAvailabilityForPossibleJobs;
      const user = this.user as PoolerUser;
      let customer: any = doc.customer;
      const customerIds = Object.keys(user.customerInformation || {}) || [];
      if (
        !customerIds.includes(doc.customer.id) ||
        !user.customerInformation?.[doc.customer.id]?.departmentIds?.includes(
          doc.department.id,
        )
      ) {
        customerIds?.forEach((customerId) => {
          const transfer = doc.transfers?.find(
            (transfer) =>
              transfer.targetCustomer.id === customerId &&
              transfer.transferredAt,
          );
          if (
            transfer &&
            user.customerInformation?.[customerId]?.departmentIds?.includes(
              doc.department.id,
            )
          ) {
            customer = {
              id: customerId,
              displayName: transfer.targetCustomer.displayName,
            };
          }
        });
      }
      doc.yourself = {
        status: InviteeStatusEnum.OPEN,
        userId: Meteor.userId(),
        customer: customer,
        selfSignIn: true,
      } as Invitee;
    }
    // @ts-ignore
    delete doc.invitees;
    return doc;
  }

  transform(doc: Job) {
    if (doc.invitees) {
      if (this.isPooler) {
        doc.yourself = doc.invitees.find(
          (invitee: Invitee) => invitee.userId == Meteor.userId(),
        );
        // @ts-ignore
        delete doc.invitees;
      }
    }
    return doc;
  }

  ngOnDestroy(): void {
    if (this.myUserSubscription) {
      this.myUserSubscription.unsubscribe();
    }
    if (this.organisationRequestSubscription) {
      this.organisationRequestSubscription.unsubscribe();
    }
    if (
      this.meteorLiveQueryHandlers &&
      this.meteorLiveQueryHandlers.length > 0
    ) {
      this.meteorLiveQueryHandlers.forEach((liveQueryHandler) => {
        liveQueryHandler.stop();
      });
    }
    if (this.userTracker) {
      this.userTracker.stop();
    }
    if (this.jobsTracker) {
      this.jobsTracker.stop();
    }
    if (this.possibleJobsTracker) {
      this.possibleJobsTracker.stop();
    }
  }

  trackById(index: number, object: Employment | Job): string {
    return object._id;
  }

  switchPossibleJobsTab(
    value: PossibleJobsTab,
    scrollToElement?: HTMLDivElement,
  ) {
    const currentScrollPosition = window.scrollY;
    if (scrollToElement) {
      // timeout is needed for scrolling because we interfere with the angular "scrollPositionRestoration" here
    }
    this.possibleJobsTab$.next(value);
  }

  trackPoolRequest(_index: number, poolRequest: any) {
    return poolRequest.customerId;
  }

  readonly RoleEnum = RoleEnum;
}
